import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaLock, FaEnvelope, FaPhone, FaChevronDown, FaCheck, FaCalendar, FaUser, FaShieldAlt, FaBriefcase, FaEye, FaEyeSlash } from 'react-icons/fa';
import Toast from '../components/Toast';
import * as authService from '../services/authService';
import * as masterService from '../services/masterService';

const AdminUserSignup = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        role: '',
        name: '',
        dateOfBirth: '',
        university: '',
        college: '',
        state: '',
        phoneNumber: '',
        email: '',
        password: '',
        confirmPassword: '',
        department: '',
        stream: '',
        specialization: '',
        designation: '',
        otherUniversityName: '',
        otherDepartmentName: '',
        otherStreamName: '',
        otherSpecializationName: '',
        specializations: [], // For multiple selections
    });

    const [subjects, setSubjects] = useState([]);
    const [streams, setStreams] = useState([]);
    const [specializations, setSpecializations] = useState([]);

    const [searchUniversity, setSearchUniversity] = useState('');
    const [showUniversityDropdown, setShowUniversityDropdown] = useState(false);
    const [searchState, setSearchState] = useState('');
    const [showStateDropdown, setShowStateDropdown] = useState(false);

    const [searchDepartment, setSearchDepartment] = useState('');
    const [showDepartmentDropdown, setShowDepartmentDropdown] = useState(false);
    const [searchStream, setSearchStream] = useState('');
    const [showStreamDropdown, setShowStreamDropdown] = useState(false);
    const [searchSpecialization, setSearchSpecialization] = useState('');
    const [showSpecializationDropdown, setShowSpecializationDropdown] = useState(false);

    const [universityList, setUniversityList] = useState([]); // Will store {name, state} objects
    const [stateList, setStateList] = useState([]);

    // Use state-based filtered list for universities since it comes from API search
    const filteredUniversities = universityList;

    const filteredStates = stateList.filter(s =>
        s.toLowerCase().includes(searchState.toLowerCase())
    );

    const filteredDepartments = subjects.filter(s => {
        const name = s.name || s;
        return typeof name === 'string' && name.toLowerCase().includes(searchDepartment.toLowerCase());
    });

    const filteredStreams = streams.filter(m => {
        const name = m.name || m;
        const streamSubjectId = m.subject_id || m.subject?.id;
        const matchesDepartment = !formData.department || streamSubjectId === formData.department;
        return typeof name === 'string' && name.toLowerCase().includes(searchStream.toLowerCase()) && matchesDepartment;
    });

    const filteredSpecs = specializations.filter(sp => {
        const name = sp.name || sp;
        const specStreamId = sp.stream_id || sp.stream?.id;
        const matchesStream = !formData.stream || specStreamId === formData.stream;
        return typeof name === 'string' && name.toLowerCase().includes(searchSpecialization.toLowerCase()) && matchesStream;
    });

    // Add "Other" option to departments
    const departmentsWithOther = [...filteredDepartments];
    if (!departmentsWithOther.some(d => (d.name || d) === 'Other')) {
        departmentsWithOther.push({ id: 'other', name: 'Other' });
    }

    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [verification, setVerification] = useState({
        phoneVerified: false,
        phoneOtp: '',
        phoneOtpSent: false,
        emailVerified: false,
        emailOtp: '',
        emailOtpSent: false,
    });

    const [toast, setToast] = useState(null);
    const [isPhoneLoading, setIsPhoneLoading] = useState(false);
    const [isEmailLoading, setIsEmailLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [roles, setRoles] = useState([]);
    const [loadingRoles, setLoadingRoles] = useState(true);

    // Master data loading functions moved to component scope for reuse
    const loadUniversitiesAndStates = async () => {
        try {
            const response = await masterService.getAllUniversities();
            let rawData = [];

            if (response.success && Array.isArray(response.data)) {
                rawData = response.data;
            } else if (Array.isArray(response)) {
                rawData = response;
            }

            if (rawData.length > 0) {
                // Extract unique state names for fallback
                const states = rawData.map(u => u.state).filter(Boolean);
                setStateList([...new Set(states)].sort());

                // Initial university list
                setUniversityList(rawData.slice(0, 20));
            }
        } catch (error) {
            console.error('Error loading universities:', error);
        }
    };

    const loadMasterData = async () => {
        try {
            const response = await masterService.masterSearchAll();
            const data = response.success ? response.data : response;

            if (data) {
                // Mapping according to user requirement: subject is department
                setSubjects(data.subjects || []);
                setStreams(data.streams || []);
                setSpecializations(data.specializations || []);
            }
        } catch (error) {
            console.error('Error loading master data:', error);
        }
    };

    // Fetch roles from API and filter for admin and user only
    useEffect(() => {
        const loadRoles = async () => {
            try {
                setLoadingRoles(true);
                const rolesData = await authService.fetchLoginRoles();

                // Filter to show only admin and user roles
                const filteredRoles = rolesData.filter(role =>
                    role.code === 'admin' || role.code === 'user'
                );

                setRoles(filteredRoles);
            } catch (error) {
                console.error('Error fetching roles:', error);
                showToast('Failed to load roles. Please refresh the page.', 'error');
                // Fallback roles
                setRoles([
                    { id: 'admin', description: 'Admin', code: 'admin' },
                    { id: 'user', description: 'User', code: 'user' },
                ]);
            } finally {
                setLoadingRoles(false);
            }
        };

        loadRoles();
        loadUniversitiesAndStates();
        loadMasterData();
    }, []);

    // University Search Effect
    useEffect(() => {
        const delayDebounceFn = setTimeout(async () => {
            if (searchUniversity && searchUniversity.length >= 2) {
                // Don't search if the search text matches the selected university exactly
                if (formData.university === searchUniversity) return;

                try {
                    const response = await masterService.searchUniversities(searchUniversity);
                    let results = [];
                    if (response.success && Array.isArray(response.data)) {
                        results = response.data;
                    } else if (Array.isArray(response)) {
                        results = response;
                    }

                    // Add "Other" to results
                    if (!results.some(u => (u.name || u) === "Other")) {
                        results.push({ name: "Other", state: "" });
                    }
                    setUniversityList(results);
                    setShowUniversityDropdown(true);
                } catch (error) {
                    console.error('Error searching universities:', error);
                }
            } else if (!searchUniversity) {
                // Reset to initial list when cleared
                loadUniversitiesAndStates();
            }
        }, 500);

        return () => clearTimeout(delayDebounceFn);
    }, [searchUniversity, formData.university]);

    // Master Data Search Effect (Department, Stream, Specialization)
    useEffect(() => {
        const delayDebounceFn = setTimeout(async () => {
            // Handle Department Search - only if we don't have a selection
            if (searchDepartment && searchDepartment.length >= 2 && !formData.department) {
                try {
                    const response = await masterService.masterSearch(searchDepartment);
                    const data = response.success ? response.data : response;
                    if (data && data.subjects) setSubjects(data.subjects);
                } catch (error) {
                    console.error('Error searching departments:', error);
                }
            }

            // Handle Stream Search - only if we don't have a selection
            if (searchStream && searchStream.length >= 2 && !formData.stream) {
                try {
                    const response = await masterService.masterSearch(searchStream);
                    const data = response.success ? response.data : response;
                    if (data && data.streams) setStreams(data.streams);
                } catch (error) {
                    console.error('Error searching streams:', error);
                }
            }

            // Handle Specialization Search - only if we don't have a selection
            if (searchSpecialization && searchSpecialization.length >= 2 && !formData.specialization) {
                try {
                    const response = await masterService.masterSearch(searchSpecialization);
                    const data = response.success ? response.data : response;
                    if (data && data.specializations) setSpecializations(data.specializations);
                } catch (error) {
                    console.error('Error searching specializations:', error);
                }
            }

            // Reset to full list if all search strings are empty
            if (!searchDepartment && !searchStream && !searchSpecialization) {
                loadMasterData();
            }
        }, 500);

        return () => clearTimeout(delayDebounceFn);
    }, [searchDepartment, searchStream, searchSpecialization]);

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name === 'phoneNumber') {
            const numericValue = value.replace(/\D/g, '').slice(0, 10);
            setFormData(prev => ({ ...prev, [name]: numericValue }));

            // Reset verification if phone changes
            setVerification(prev => ({
                ...prev,
                phoneVerified: false,
                phoneOtpSent: false,
                phoneOtp: ''
            }));
            return;
        }

        if (name === 'dateOfBirth') {
            let val = value.replace(/\D/g, '');
            if (val.length > 8) val = val.slice(0, 8);

            let formatted = '';
            if (val.length > 0) {
                formatted += val.slice(0, 2);
                if (val.length > 2) {
                    formatted += '-' + val.slice(2, 4);
                    if (val.length > 4) {
                        formatted += '-' + val.slice(4, 8);
                    }
                }
            }
            setFormData(prev => ({ ...prev, dateOfBirth: formatted }));
            return;
        }

        setFormData({
            ...formData,
            [name]: value,
        });

        if (name === 'email') {
            setVerification(prev => ({
                ...prev,
                emailVerified: false,
                emailOtpSent: false,
                emailOtp: ''
            }));
        }
    };

    const showToast = (message, type = 'success', persistent = false) => {
        setToast({
            message,
            type,
            duration: persistent ? null : 3000
        });
    };

    const clearToast = () => setToast(null);

    const isValidEmail = (email) => {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    };

    const handleSendPhoneOtp = async () => {
        if (!formData.phoneNumber || formData.phoneNumber.length !== 10) {
            showToast('Please enter a valid 10-digit phone number', 'error');
            return;
        }

        setIsPhoneLoading(true);
        try {
            // First check if phone number exists
            const checkResponse = await authService.checkPhone(formData.phoneNumber);
            console.log('Check phone response:', checkResponse);

            // If phone exists, the API should throw an error, but let's also check the response
            if (checkResponse && (checkResponse.exists === true || checkResponse.available === false)) {
                showToast('Phone number already exists. Please login instead.', 'error');
                return;
            }

            await authService.sendPhoneOtp(formData.phoneNumber);

            setVerification({ ...verification, phoneOtpSent: true });
            showToast('OTP sent successfully!', 'success');
        } catch (error) {
            console.error('Error sending phone OTP:', error);
            showToast(error.message || 'Failed to send OTP. Please try again.', 'error');
        } finally {
            setIsPhoneLoading(false);
        }
    };

    const handleVerifyPhone = async () => {
        if (!verification.phoneOtp || verification.phoneOtp.length !== 6) {
            showToast('Please enter a valid 6-digit OTP', 'error');
            return;
        }

        setIsPhoneLoading(true);
        try {
            await authService.verifyPhoneOtp(formData.phoneNumber, verification.phoneOtp);

            setVerification({ ...verification, phoneVerified: true });
            clearToast();
            showToast('Phone number verified successfully!', 'success');
        } catch (error) {
            console.error('Error verifying phone OTP:', error);
            showToast(error.message || 'Failed to verify OTP. Please try again.', 'error');
        } finally {
            setIsPhoneLoading(false);
        }
    };

    const handleSendEmailOtp = async () => {
        if (!formData.email || !formData.email.includes('@')) {
            showToast('Please enter a valid email address', 'error');
            return;
        }

        setIsEmailLoading(true);
        try {
            // First check if email exists
            const checkResponse = await authService.checkEmail(formData.email);
            console.log('Check email response:', checkResponse);

            // If email exists, the API should throw an error, but let's also check the response
            if (checkResponse && (checkResponse.exists === true || checkResponse.available === false)) {
                showToast('Email already exists. Please login instead.', 'error');
                return;
            }

            await authService.sendEmailOtp(formData.email);

            setVerification({ ...verification, emailOtpSent: true });
            showToast('OTP sent to your email. Please check your inbox.', 'success');
        } catch (error) {
            console.error('Error sending email OTP:', error);
            showToast(error.message || 'Failed to send OTP. Please try again.', 'error');
        } finally {
            setIsEmailLoading(false);
        }
    };

    const handleVerifyEmail = async () => {
        if (!verification.emailOtp || verification.emailOtp.length !== 6) {
            showToast('Please enter a valid 6-digit OTP', 'error');
            return;
        }

        setIsEmailLoading(true);
        try {
            await authService.verifyEmailOtp(formData.email, verification.emailOtp);

            setVerification({ ...verification, emailVerified: true });
            clearToast();
            showToast('Email verified successfully!', 'success');
        } catch (error) {
            console.error('Error verifying email OTP:', error);
            showToast(error.message || 'Failed to verify OTP. Please try again.', 'error');
        } finally {
            setIsEmailLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!verification.phoneVerified) {
            showToast('Please verify your phone number', 'error');
            return;
        }

        if (!formData.email || !isValidEmail(formData.email)) {
            showToast('Please enter a valid email address', 'error');
            return;
        }

        /*
                if (!formData.university) {
                    showToast('Please select a University from the list', 'error');
                    return;
                }
        
                if (!formData.state) {
                    showToast('Please select a State from the list', 'error');
                    return;
                }
                */

        if (formData.password !== formData.confirmPassword) {
            showToast('Passwords do not match', 'error');
            return;
        }

        if (!formData.dateOfBirth || !formData.dateOfBirth.includes('-')) {
            showToast('Please enter Date of Birth in DD-MM-YYYY format', 'error');
            return;
        }

        const [day, month, year] = formData.dateOfBirth.split('-');
        if (!day || !month || !year || year.length !== 4) {
            showToast('Invalid Date of Birth format. Use DD-MM-YYYY', 'error');
            return;
        }

        if (parseInt(year) > 2005) {
            showToast('You must be born in 2005 or earlier', 'error');
            return;
        }

        setIsSubmitting(true);

        try {
            // Build base payload with required fields
            const registrationData = {
                role_id: formData.role || null,
                full_name: formData.name,
                date_of_birth: `${year}-${month}-${day}`,
                university: formData.university === 'Other' ? formData.otherUniversityName : formData.university,
                state: formData.state,
                phone_number: formData.phoneNumber,
                email: formData.email,
                password: formData.password,
                confirm_password: formData.confirmPassword,
                is_reviewer: 0,
            };

            // Add optional fields only if they have values
            if (formData.college) registrationData.college = formData.college;
            if (formData.designation) registrationData.designation = formData.designation;

            // Handle department/stream/specialization
            if (formData.department === 'other') {
                if (formData.otherDepartmentName) registrationData.other_department = formData.otherDepartmentName;
                if (formData.otherStreamName) registrationData.other_stream = formData.otherStreamName;
                if (formData.otherSpecializationName) registrationData.other_specialization = formData.otherSpecializationName;
            } else {
                if (formData.department) registrationData.subject_id = formData.department;
                if (formData.stream) registrationData.stream_id = formData.stream;

                // Handle specializations
                if (Array.isArray(formData.specializations) && formData.specializations.length > 0) {
                    registrationData.specialization_ids = formData.specializations;
                    registrationData.specialization_id = formData.specializations[0]; // Primary specialization
                } else if (formData.specialization) {
                    registrationData.specialization_id = formData.specialization;
                }
            }

            console.log('EMPLOYEE REGISTER PAYLOAD 👉', registrationData);

            await authService.registerUser(registrationData);

            showToast('Account created successfully! Logging you in...', 'success');

            // Auto-login logic
            try {
                const loginData = {
                    role_id: formData.role,
                    username: formData.phoneNumber || formData.email,
                    password: formData.password,
                };

                const loginResponse = await authService.loginUser(loginData);

                if (loginResponse.success) {
                    const apiData = loginResponse.data || {};
                    const userObj = apiData.user || apiData;

                    const userData = {
                        ...userObj,
                        id: userObj.id || userObj.user_id,
                        role: userObj.role_code || userObj.role || (roles.find(r => r.id === formData.role)?.code) || 'user',
                        is_reviewer: !!userObj.is_reviewer || !!userObj.permissions?.is_reviewer,
                        is_editorial_board: !!userObj.is_editorial_board || !!userObj.permissions?.is_editorial_board,
                        full_name: userObj.full_name || userObj.name || formData.name || userObj.username,
                        name: userObj.name || formData.name || userObj.full_name,
                        loginTime: new Date().toISOString(),
                    };

                    authService.storeSession(userData, null, null, true);
                    window.dispatchEvent(new Event('userLogin'));

                    setTimeout(() => navigate('/'), 1500);
                    return;
                }
                // Fallback for failed auto-login but successful reg
                setTimeout(() => navigate('/'), 2000);
            } catch (loginError) {
                console.error('Auto-login failed:', loginError);
                // Still redirect to home if registration was successful
                setTimeout(() => navigate('/'), 2000);
            }
        } catch (error) {
            console.error('Error during employee registration:', error);
            // Extract specific validation errors if available
            let errorMsg = 'Registration failed. Please try again.';
            if (error.errors) {
                const firstError = Object.values(error.errors)[0];
                if (Array.isArray(firstError)) errorMsg = firstError[0];
                else if (typeof firstError === 'string') errorMsg = firstError;
            } else if (error.message) {
                errorMsg = error.message;
            }
            showToast(errorMsg, 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            {toast && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    duration={toast.duration}
                    onClose={() => setToast(null)}
                />
            )}

            <div className="min-h-screen flex">
                <div className="hidden lg:block lg:w-2/5 fixed left-0 top-0 h-screen">
                    <div className="absolute inset-0 bg-gradient-to-br from-slate-900/90 via-indigo-900/90 to-slate-900/90 z-10"></div>
                    <img
                        src="https://images.unsplash.com/photo-1557804506-669a67965ba0?w=800&q=80"
                        alt="Signup"
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 z-20 flex flex-col items-center justify-center text-white p-12">
                        <FaShieldAlt className="text-6xl mb-6" />
                        <h2 className="text-4xl font-bold mb-4 text-center">Admin & User Portal</h2>
                        <p className="text-xl text-center max-w-md mb-8">
                            Secure access for administrators and users
                        </p>
                        <div className="space-y-4 w-full max-w-sm">
                            <div className="flex items-center space-x-3 bg-white/10 backdrop-blur-sm rounded-lg p-4">
                                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                                    <FaCheck />
                                </div>
                                <div>
                                    <div className="font-semibold">Secure Access</div>
                                    <div className="text-sm text-gray-200">Role-based authentication</div>
                                </div>
                            </div>
                            <div className="flex items-center space-x-3 bg-white/10 backdrop-blur-sm rounded-lg p-4">
                                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                                    <FaCheck />
                                </div>
                                <div>
                                    <div className="font-semibold">Verified Identity</div>
                                    <div className="text-sm text-gray-200">Phone & email verification</div>
                                </div>
                            </div>
                            <div className="flex items-center space-x-3 bg-white/10 backdrop-blur-sm rounded-lg p-4">
                                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                                    <FaCheck />
                                </div>
                                <div>
                                    <div className="font-semibold">Full Control</div>
                                    <div className="text-sm text-gray-200">Complete system access</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="w-full lg:w-3/5 lg:ml-[40%] min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-white p-6 overflow-y-auto">
                    <div className="max-w-3xl w-full py-8">
                        <div className="lg:hidden text-center mb-6">
                            <FaShieldAlt className="text-5xl mx-auto mb-4 text-purple-600" />
                        </div>

                        <div className="bg-white rounded-2xl shadow-2xl p-6 md:p-10">
                            <div className="text-center mb-8">
                                <h2 className="text-3xl font-bold text-gray-900">Employee Registration</h2>
                                <p className="text-gray-600 mt-2">Create your administrative or user account</p>
                            </div>

                            <form onSubmit={handleSubmit} className="space-y-5">
                                <div>
                                    <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-2">
                                        Select Role *
                                    </label>
                                    <div className="relative">
                                        <select
                                            id="role"
                                            name="role"
                                            value={formData.role}
                                            onChange={handleChange}
                                            required
                                            disabled={isSubmitting || loadingRoles}
                                            className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-purple-600 appearance-none bg-white transition-all disabled:opacity-50"
                                        >
                                            <option value="">
                                                {loadingRoles ? 'Loading roles...' : 'Choose your role'}
                                            </option>
                                            {roles.map((role) => (
                                                <option key={role.id} value={role.id}>
                                                    {role.description || role.label || role.code}
                                                </option>
                                            ))}
                                        </select>
                                        <FaChevronDown className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
                                    </div>
                                </div>

                                {/* designation for Professor and Reviewer */}
                                {/* 
                                {(() => {
                                    const selectedRole = roles.find(r => r.id === formData.role);
                                    const isProfessorOrReviewer = selectedRole && (selectedRole.code === 'professor' || selectedRole.code === 'reviewer');
                                    if (isProfessorOrReviewer) {
                                        return (
                                            <div className="animate-fade-in">
                                                <label htmlFor="designation" className="block text-sm font-medium text-gray-700 mb-2">
                                                    Designation *
                                                </label>
                                                <div className="relative">
                                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                        <FaBriefcase className="text-gray-400" />
                                                    </div>
                                                    <input
                                                        type="text"
                                                        id="designation"
                                                        name="designation"
                                                        value={formData.designation}
                                                        onChange={handleChange}
                                                        required
                                                        className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-purple-600 transition-all"
                                                        placeholder="Enter your designation"
                                                    />
                                                </div>
                                            </div>
                                        );
                                    }
                                    return null;
                                })()}
                                */}


                                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                    <div>
                                        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                                            Full Name *
                                        </label>
                                        <div className="relative">
                                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                <FaUser className="text-gray-400" />
                                            </div>
                                            <input
                                                type="text"
                                                id="name"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleChange}
                                                required
                                                disabled={isSubmitting}
                                                className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-purple-600 transition-all disabled:opacity-50"
                                                placeholder="Enter your full name"
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label htmlFor="dateOfBirth" className="block text-sm font-medium text-gray-700 mb-2">
                                            Date of Birth *
                                        </label>
                                        <div className="relative">
                                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                <FaCalendar className="text-gray-400" />
                                            </div>
                                            <input
                                                type="text"
                                                id="dateOfBirth"
                                                name="dateOfBirth"
                                                value={formData.dateOfBirth}
                                                onChange={handleChange}
                                                required
                                                maxLength="10"
                                                placeholder="DD-MM-YYYY"
                                                disabled={isSubmitting}
                                                className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-purple-600 transition-all disabled:opacity-50"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* 
                                <div className={`grid grid-cols-1 ${formData.university === 'Other' ? 'md:grid-cols-2' : 'md:grid-cols-3'} gap-5`}>
                                    <div className="relative">
                                        <label htmlFor="university" className="block text-sm font-medium text-gray-700 mb-2">
                                            University *
                                        </label>
                                        <input
                                            type="text"
                                            placeholder="Search & Select University"
                                            value={formData.university || searchUniversity}
                                            onChange={(e) => {
                                                setSearchUniversity(e.target.value);
                                                setFormData({ ...formData, university: '' });
                                                setShowUniversityDropdown(true);
                                            }}
                                            onFocus={() => setShowUniversityDropdown(true)}
                                            onBlur={() => {
                                                setTimeout(() => setShowUniversityDropdown(false), 200);
                                            }}
                                            className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-purple-600 transition-all"
                                            required
                                        />
                                        {showUniversityDropdown && (
                                            <div className="absolute z-50 w-full mt-1 bg-white border-2 border-gray-100 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                                                {filteredUniversities.length > 0 ? (
                                                    filteredUniversities.map((u, index) => (
                                                        <div
                                                            key={index}
                                                            className="px-4 py-3 hover:bg-purple-50 cursor-pointer text-sm transition-colors border-b border-gray-50 last:border-0"
                                                            onMouseDown={() => {
                                                                const uName = u.name || u;
                                                                const uState = u.state || '';
                                                                setFormData({
                                                                    ...formData,
                                                                    university: uName,
                                                                    state: uState || formData.state,
                                                                    otherUniversityName: uName === 'Other' ? '' : formData.otherUniversityName
                                                                });
                                                                setSearchUniversity(uName);
                                                                if (uState) setSearchState(uState);
                                                                setShowUniversityDropdown(false);
                                                            }}
                                                        >
                                                            {u.name || u}
                                                        </div>
                                                    ))
                                                ) : (
                                                    <div className="px-4 py-3 text-gray-500 text-sm border-b border-gray-50 cursor-pointer hover:bg-purple-50"
                                                        onMouseDown={() => {
                                                            setFormData({ ...formData, university: 'Other' });
                                                            setSearchUniversity('Other');
                                                            setShowUniversityDropdown(false);
                                                        }}
                                                    >
                                                        Other (Type manually)
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    {formData.university === 'Other' && (
                                        <div className="animate-fade-in">
                                            <label htmlFor="otherUniversityName" className="block text-sm font-medium text-gray-700 mb-2">
                                                Specify University *
                                            </label>
                                            <input
                                                type="text"
                                                id="otherUniversityName"
                                                name="otherUniversityName"
                                                value={formData.otherUniversityName}
                                                onChange={handleChange}
                                                required
                                                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-purple-600 transition-all font-medium"
                                                placeholder="Enter university name"
                                            />
                                        </div>
                                    )}

                                    <div>
                                        <label htmlFor="college" className="block text-sm font-medium text-gray-700 mb-2">
                                            College Name
                                        </label>
                                        <input
                                            type="text"
                                            id="college"
                                            name="college"
                                            value={formData.college}
                                            onChange={handleChange}
                                            className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-purple-600 transition-all"
                                            placeholder="Enter your college name (optional)"
                                        />
                                    </div>

                                    <div className="relative">
                                        <label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-2">
                                            State *
                                        </label>
                                        <input
                                            type="text"
                                            placeholder="Search & Select State"
                                            value={formData.state || searchState}
                                            onChange={(e) => {
                                                setSearchState(e.target.value);
                                                setFormData({ ...formData, state: '' });
                                                setShowStateDropdown(true);
                                            }}
                                            onFocus={() => setShowStateDropdown(true)}
                                            onBlur={() => {
                                                setTimeout(() => setShowStateDropdown(false), 200);
                                            }}
                                            className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-purple-600 transition-all"
                                            required
                                        />
                                        {showStateDropdown && (
                                            <div className="absolute z-50 w-full mt-1 bg-white border-2 border-gray-100 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                                                {filteredStates.length > 0 ? (
                                                    filteredStates.map((s, index) => (
                                                        <div
                                                            key={index}
                                                            className="px-4 py-3 hover:bg-purple-50 cursor-pointer text-sm transition-colors border-b border-gray-50 last:border-0"
                                                            onMouseDown={() => {
                                                                setFormData({ ...formData, state: s });
                                                                setSearchState(s);
                                                                setShowStateDropdown(false);
                                                            }}
                                                        >
                                                            {s}
                                                        </div>
                                                    ))
                                                ) : (
                                                    <div className="px-4 py-3 text-gray-500 text-sm">No state found</div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                */}

                                {/* 
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                                    <div className="relative">
                                        <label htmlFor="department" className="block text-sm font-medium text-gray-700 mb-2">
                                            Department
                                        </label>
                                        <input
                                            type="text"
                                            placeholder="Search & Select Department"
                                            value={subjects.find(s => s.id === formData.department)?.name || searchDepartment}
                                            onChange={(e) => {
                                                setSearchDepartment(e.target.value);
                                                setFormData({ ...formData, department: '' });
                                                setShowDepartmentDropdown(true);
                                            }}
                                            onFocus={() => setShowDepartmentDropdown(true)}
                                            onBlur={() => setTimeout(() => setShowDepartmentDropdown(false), 200)}
                                            className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-purple-600 transition-all"
                                        />
                                        {showDepartmentDropdown && (
                                            <div className="absolute z-50 w-full mt-1 bg-white border-2 border-gray-100 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                                                {departmentsWithOther.length > 0 ? (
                                                    departmentsWithOther.map((s, index) => (
                                                        <div
                                                            key={index}
                                                            className="px-4 py-3 hover:bg-purple-50 cursor-pointer text-sm transition-colors border-b border-gray-50 last:border-0"
                                                            onMouseDown={() => {
                                                                setFormData({
                                                                    ...formData,
                                                                    department: s.id,
                                                                    stream: '',
                                                                    specialization: '',
                                                                    otherDepartmentName: s.id === 'other' ? '' : formData.otherDepartmentName,
                                                                    otherStreamName: '',
                                                                    otherSpecializationName: ''
                                                                });
                                                                setSearchDepartment(s.name || s);
                                                                setSearchStream('');
                                                                setSearchSpecialization('');
                                                                setShowDepartmentDropdown(false);
                                                                loadMasterData(); // Restore all lists for next selection
                                                            }}
                                                        >
                                                            {s.name || s}
                                                        </div>
                                                    ))
                                                ) : (
                                                    <div className="px-4 py-3 text-gray-500 text-sm">No department found</div>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    {formData.department === 'other' && (
                                        <div className="col-span-full grid grid-cols-1 md:grid-cols-3 gap-5 animate-fade-in bg-gray-50 p-4 rounded-xl border-2 border-dashed border-gray-200">
                                            <div>
                                                <label htmlFor="otherDepartmentName" className="block text-sm font-medium text-gray-700 mb-2">
                                                    Department Name *
                                                </label>
                                                <input
                                                    type="text"
                                                    id="otherDepartmentName"
                                                    name="otherDepartmentName"
                                                    value={formData.otherDepartmentName}
                                                    onChange={handleChange}
                                                    required
                                                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-purple-600 transition-all bg-white"
                                                    placeholder="Enter department"
                                                />
                                            </div>
                                            <div>
                                                <label htmlFor="otherStreamName" className="block text-sm font-medium text-gray-700 mb-2">
                                                    Stream Name *
                                                </label>
                                                <input
                                                    type="text"
                                                    id="otherStreamName"
                                                    name="otherStreamName"
                                                    value={formData.otherStreamName}
                                                    onChange={handleChange}
                                                    required
                                                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-purple-600 transition-all bg-white"
                                                    placeholder="Enter stream"
                                                />
                                            </div>
                                            <div>
                                                <label htmlFor="otherSpecializationName" className="block text-sm font-medium text-gray-700 mb-2">
                                                    Specialization Name *
                                                </label>
                                                <input
                                                    type="text"
                                                    id="otherSpecializationName"
                                                    name="otherSpecializationName"
                                                    value={formData.otherSpecializationName}
                                                    onChange={handleChange}
                                                    required
                                                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-purple-600 transition-all bg-white"
                                                    placeholder="Enter specialization"
                                                />
                                            </div>
                                        </div>
                                    )}

                                    {formData.department !== 'other' && (
                                        <>
                                            <div className="relative">
                                                <label htmlFor="stream" className="block text-sm font-medium text-gray-700 mb-2">
                                                    Stream
                                                </label>
                                                <input
                                                    type="text"
                                                    placeholder={formData.department ? "Search & Select Stream" : "Select Department First"}
                                                    value={streams.find(m => m.id === formData.stream)?.name || searchStream}
                                                    onChange={(e) => {
                                                        setSearchStream(e.target.value);
                                                        setFormData({ ...formData, stream: '' });
                                                        setShowStreamDropdown(true);
                                                    }}
                                                    onFocus={() => formData.department && setShowStreamDropdown(true)}
                                                    onBlur={() => setTimeout(() => setShowStreamDropdown(false), 200)}
                                                    className={`w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-purple-600 transition-all ${!formData.department ? 'bg-gray-50 cursor-not-allowed text-gray-400' : ''}`}
                                                    disabled={!formData.department}
                                                />
                                                {showStreamDropdown && (
                                                    <div className="absolute z-50 w-full mt-1 bg-white border-2 border-gray-100 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                                                        {filteredStreams.length > 0 ? (
                                                            filteredStreams.map((m, index) => (
                                                                <div
                                                                    key={index}
                                                                    className="px-4 py-3 hover:bg-purple-50 cursor-pointer text-sm transition-colors border-b border-gray-50 last:border-0"
                                                                    onMouseDown={() => {
                                                                        setFormData({
                                                                            ...formData,
                                                                            stream: m.id,
                                                                            specialization: ''
                                                                        });
                                                                        setSearchStream(m.name || m);
                                                                        setSearchSpecialization('');
                                                                        setShowStreamDropdown(false);
                                                                        loadMasterData(); // Restore all lists for next selection
                                                                    }}
                                                                >
                                                                    {m.name || m}
                                                                </div>
                                                            ))
                                                        ) : (
                                                            <div className="px-4 py-3 text-gray-500 text-sm">No stream found</div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>

                                            <div className="relative">
                                                {(() => {
                                                    const selectedRole = roles.find(r => r.id === formData.role);
                                                    const isProfessorOrReviewer = selectedRole && (selectedRole.code === 'professor' || selectedRole.code === 'reviewer');

                                                    return (
                                                        <>
                                                            <label htmlFor="specialization" className="block text-sm font-medium text-gray-700 mb-2">
                                                                Specialization {isProfessorOrReviewer && '(Multi-select)'}
                                                            </label>
                                                            {isProfessorOrReviewer && formData.specializations.length > 0 && (
                                                                <div className="flex flex-wrap gap-1 mb-2">
                                                                    {formData.specializations.map(id => {
                                                                        const spec = specializations.find(s => s.id === id);
                                                                        return (
                                                                            <span key={id} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                                                                {spec?.name || id}
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={() => setFormData(prev => ({
                                                                                        ...prev,
                                                                                        specializations: prev.specializations.filter(sid => sid !== id)
                                                                                    }))}
                                                                                    className="ml-1 text-purple-400 hover:text-purple-600"
                                                                                >
                                                                                    &times;
                                                                                </button>
                                                                            </span>
                                                                        );
                                                                    })}
                                                                </div>
                                                            )}
                                                            <input
                                                                type="text"
                                                                placeholder={formData.stream ? (isProfessorOrReviewer ? "Search & Add Multiple" : "Search & Select Specialization") : "Select Stream First"}
                                                                value={!isProfessorOrReviewer ? (specializations.find(sp => sp.id === formData.specialization)?.name || searchSpecialization) : searchSpecialization}
                                                                onChange={(e) => {
                                                                    setSearchSpecialization(e.target.value);
                                                                    if (!isProfessorOrReviewer) setFormData({ ...formData, specialization: '' });
                                                                    setShowSpecializationDropdown(true);
                                                                }}
                                                                onFocus={() => formData.stream && setShowSpecializationDropdown(true)}
                                                                onBlur={() => setTimeout(() => setShowSpecializationDropdown(false), 200)}
                                                                className={`w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-purple-600 transition-all ${!formData.stream ? 'bg-gray-50 cursor-not-allowed text-gray-400' : ''}`}
                                                                disabled={!formData.stream}
                                                            />
                                                            {showSpecializationDropdown && (
                                                                <div className="absolute z-50 w-full mt-1 bg-white border-2 border-gray-100 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                                                                    {filteredSpecs.length > 0 ? (
                                                                        filteredSpecs.map((sp, index) => (
                                                                            <div
                                                                                key={index}
                                                                                className="px-4 py-3 hover:bg-purple-50 cursor-pointer text-sm transition-colors border-b border-gray-50 last:border-0"
                                                                                onMouseDown={() => {
                                                                                    if (isProfessorOrReviewer) {
                                                                                        if (!formData.specializations.includes(sp.id)) {
                                                                                            setFormData(prev => ({
                                                                                                ...prev,
                                                                                                specializations: [...prev.specializations, sp.id]
                                                                                            }));
                                                                                        }
                                                                                        setSearchSpecialization('');
                                                                                    } else {
                                                                                        setFormData({ ...formData, specialization: sp.id });
                                                                                        setSearchSpecialization(sp.name || sp);
                                                                                    }
                                                                                    setShowSpecializationDropdown(false);
                                                                                    loadMasterData(); // Restore all lists for next selection
                                                                                }}
                                                                            >
                                                                                {sp.name || sp}
                                                                            </div>
                                                                        ))
                                                                    ) : (
                                                                        <div className="px-4 py-3 text-gray-500 text-sm">No specialization found</div>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </>
                                                    );
                                                })()}
                                            </div>
                                        </>
                                    )}
                                </div>
                                */}

                                <div>
                                    <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700 mb-2">
                                        Phone Number * <span className="text-red-500">(Verification Required)</span>
                                    </label>
                                    <div className="flex gap-2">
                                        <div className="relative flex-1">
                                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                <FaPhone className="text-gray-400" />
                                            </div>
                                            <input
                                                type="tel"
                                                id="phoneNumber"
                                                name="phoneNumber"
                                                value={formData.phoneNumber}
                                                onChange={handleChange}
                                                required
                                                maxLength="10"
                                                disabled={isSubmitting}
                                                className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-purple-600 disabled:bg-gray-50 transition-all"
                                                placeholder="Enter 10-digit number"
                                            />
                                        </div>
                                        {!verification.phoneVerified && (
                                            <button
                                                type="button"
                                                onClick={handleSendPhoneOtp}
                                                disabled={isPhoneLoading || isSubmitting}
                                                className="px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-medium rounded-lg hover:shadow-lg transition-all whitespace-nowrap disabled:opacity-50"
                                            >
                                                Send OTP
                                            </button>
                                        )}
                                        {verification.phoneVerified && (
                                            <div className="flex items-center px-4 bg-green-50 border-2 border-green-500 rounded-lg">
                                                <FaCheck className="text-green-600 text-xl" />
                                            </div>
                                        )}
                                    </div>
                                    <p className="mt-1 text-xs text-purple-600 font-medium">
                                        📱 Your phone number can be used for login
                                    </p>

                                    {verification.phoneOtpSent && !verification.phoneVerified && (
                                        <div className="mt-3 flex gap-2 animate-fade-in">
                                            <input
                                                type="text"
                                                value={verification.phoneOtp}
                                                onChange={(e) => setVerification({ ...verification, phoneOtp: e.target.value })}
                                                maxLength="6"
                                                disabled={isPhoneLoading || isSubmitting}
                                                className="flex-1 px-4 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-purple-600 disabled:opacity-50"
                                                placeholder="Enter 6-digit OTP"
                                            />
                                            <button
                                                type="button"
                                                onClick={handleVerifyPhone}
                                                disabled={isPhoneLoading || isSubmitting}
                                                className="px-6 py-2 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition-all disabled:opacity-50"
                                            >
                                                Confirm
                                            </button>
                                        </div>
                                    )}
                                </div>

                                <div>
                                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                                        Email * <span className="text-gray-500 text-xs">(Verification Optional)</span>
                                    </label>
                                    <div className="flex gap-2">
                                        <div className="relative flex-1">
                                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                <FaEnvelope className="text-gray-400" />
                                            </div>
                                            <input
                                                type="email"
                                                id="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleChange}
                                                required
                                                disabled={isSubmitting}
                                                className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-purple-600 disabled:bg-gray-50 transition-all"
                                                placeholder="Enter your email"
                                            />
                                        </div>
                                        {!verification.emailVerified && (
                                            <button
                                                type="button"
                                                onClick={handleSendEmailOtp}
                                                disabled={isEmailLoading || isSubmitting}
                                                className="px-6 py-3 bg-gray-600 text-white font-medium rounded-lg hover:bg-gray-700 transition-all whitespace-nowrap disabled:opacity-50"
                                            >
                                                Send OTP
                                            </button>
                                        )}
                                        {verification.emailVerified && (
                                            <div className="flex items-center px-4 bg-green-50 border-2 border-green-500 rounded-lg">
                                                <FaCheck className="text-green-600 text-xl" />
                                            </div>
                                        )}
                                    </div>
                                    <p className="mt-1 text-xs text-purple-600 font-medium">
                                        ✉️ Your email address can be used for login
                                    </p>

                                    {verification.emailOtpSent && !verification.emailVerified && (
                                        <div className="mt-3 flex gap-2 animate-fade-in">
                                            <input
                                                type="text"
                                                value={verification.emailOtp}
                                                onChange={(e) => setVerification({ ...verification, emailOtp: e.target.value })}
                                                maxLength="6"
                                                disabled={isEmailLoading || isSubmitting}
                                                className="flex-1 px-4 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-purple-600 disabled:opacity-50"
                                                placeholder="Enter 6-digit OTP"
                                            />
                                            <button
                                                type="button"
                                                onClick={handleVerifyEmail}
                                                disabled={isEmailLoading || isSubmitting}
                                                className="px-6 py-2 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition-all disabled:opacity-50"
                                            >
                                                Confirm
                                            </button>
                                        </div>
                                    )}
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                    <div>
                                        <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                                            Password *
                                        </label>
                                        <div className="relative">
                                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                <FaLock className="text-gray-400" />
                                            </div>
                                            <input
                                                type={showPassword ? "text" : "password"}
                                                id="password"
                                                name="password"
                                                value={formData.password}
                                                onChange={handleChange}
                                                required
                                                minLength="8"
                                                disabled={isSubmitting}
                                                className="w-full pl-10 pr-12 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-purple-600 transition-all disabled:opacity-50"
                                                placeholder="Min. 8 characters"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowPassword(!showPassword)}
                                                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-purple-600 transition-colors"
                                            >
                                                {showPassword ? <FaEyeSlash /> : <FaEye />}
                                            </button>
                                        </div>
                                    </div>

                                    <div>
                                        <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                                            Confirm Password *
                                        </label>
                                        <div className="relative">
                                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                <FaLock className="text-gray-400" />
                                            </div>
                                            <input
                                                type={showConfirmPassword ? "text" : "password"}
                                                id="confirmPassword"
                                                name="confirmPassword"
                                                value={formData.confirmPassword}
                                                onChange={handleChange}
                                                required
                                                minLength="8"
                                                disabled={isSubmitting}
                                                className="w-full pl-10 pr-12 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-purple-600 transition-all disabled:opacity-50"
                                                placeholder="Repeat password"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-purple-600 transition-colors"
                                            >
                                                {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-purple-50 border-l-4 border-purple-600 p-4 rounded-lg">
                                    <div className="flex">
                                        <div className="flex-shrink-0">
                                            <FaShieldAlt className="h-5 w-5 text-purple-600" />
                                        </div>
                                        <div className="ml-3">
                                            <p className="text-sm text-gray-700">
                                                <strong>Note:</strong> Phone verification is required. Both your registered phone number and email can be used for login.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="w-full py-4 px-4 bg-gradient-to-r from-purple-600 to-indigo-600 text-white text-lg font-semibold rounded-lg hover:shadow-xl transform hover:scale-[1.02] transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center"
                                >
                                    {isSubmitting ? (
                                        <>
                                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            Creating Account...
                                        </>
                                    ) : (
                                        'Register My Account'
                                    )}
                                </button>

                                <div className="relative">
                                    <div className="absolute inset-0 flex items-center">
                                        <div className="w-full border-t border-gray-300"></div>
                                    </div>
                                    <div className="relative flex justify-center text-sm">
                                        <span className="px-2 bg-white text-gray-500">Already have an account?</span>
                                    </div>
                                </div>

                                <div className="text-center">
                                    <Link
                                        to="/employeelogin"
                                        className="text-purple-600 hover:text-indigo-600 font-semibold text-lg"
                                    >
                                        Login Here
                                    </Link>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default AdminUserSignup;